import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { WarningCircleIcon } from '@phosphor-icons/react/dist/csr/WarningCircle';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../primitives/Button/Button';
import { ListState } from './ListState';

/**
 * Error — the list could not be loaded.
 *
 * Handoff: "escalated-hue border and icon, plain-language cause, reassurance
 * that nothing was lost, Retry (secondary with ph-arrow-clockwise)."
 *
 * The reassurance is not padding. An agent mid-way through a shift who sees a
 * red box assumes the case they just saved went with it; the sentence that says
 * otherwise is the difference between retrying and re-entering everything.
 *
 * `role="alert"` rather than a polite announcement: a list that failed to load
 * has taken the work away, which is the definition of an interruption. It is
 * set here, on the box, so the announcement happens at insertion — an alert
 * that is already in the DOM when its text changes is announced far less
 * reliably.
 */

export interface ErrorCopy {
  title?: string;
  /** Plain language, and about the cause — not a status code. */
  cause?: string;
}

export function ErrorState({ title, cause, onRetry }: ErrorCopy & { onRetry?: () => void }) {
  const { t } = useTranslation();

  return (
    <ListState
      tone="error"
      role="alert"
      icon={<WarningCircleIcon size={26} weight="regular" />}
      title={title ?? t('list.errorTitle')}
      body={
        <>
          {cause === undefined || cause === '' ? null : <p>{cause}</p>}
          <p>{t('list.errorReassurance')}</p>
        </>
      }
      {...(onRetry === undefined
        ? {}
        : {
            action: (
              <Button
                variant="secondary"
                icon={<ArrowClockwiseIcon size={16} />}
                onClick={onRetry}
              >
                {t('actions.retry')}
              </Button>
            ),
          })}
    />
  );
}
